import { Injectable, inject } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import {
  CheckStripeConnectStatusAction,
  ClearOnboardingUrlAction,
  CompleteOnboardingAction,
  LoadOnboardingStatusAction,
  OnboardingStatus,
  ResetStripeConnectStateAction,
  SetOnboardingUrlAction,
  SetStripeConnectErrorAction,
  SetStripeConnectStatusAction,
  StartOnboardingAction,
  StripeConnectStatus,
} from './stripe-connect.actions';
import { OrganizersService } from '../services/organizers.service';
import { StripeCheckFailure } from '../domains/a-connect-account-no-retry-can-fix';
import { catchError, tap, throwError } from 'rxjs';

export interface StripeConnectStateModel {
  accountStatus: StripeConnectStatus | null;
  onboardingUrl: string | null;
  onboardingUrlExpiresAt: number | null;
  isLoading: boolean;
  error: string | null;
}

const defaults: StripeConnectStateModel = {
  accountStatus: null,
  onboardingUrl: null,
  onboardingUrlExpiresAt: null,
  isLoading: false,
  error: null,
};

/**
 * What the API really sends on top of what the generated client declares.
 *
 * `checkFailure` is not in the generated DTO, and that is not an oversight:
 * the client is regenerated against a running API, which nobody does for one
 * field, and the DTO carries no @ApiProperty so it would arrive as `object`
 * like `requirements` did anyway. Read off the response the same way, with
 * the same narrowness: a value this app does not know reads as absent, and
 * absent is the Stripe outage, which is today's wording.
 */
const THE_FAILURES_THIS_APP_KNOWS: StripeCheckFailure[] = ['stripe_unreachable', 'account_unusable'];

const theCheckFailure = (value: unknown): StripeCheckFailure | undefined => {
  const failure = (value as { checkFailure?: unknown })?.checkFailure;
  // Narrowed rather than cast. A value this app has never heard of becomes
  // undefined, which every reader treats as the outage, instead of being
  // promised to the compiler as one of two things it is not.
  return THE_FAILURES_THIS_APP_KNOWS.find((known) => known === failure);
};

/** What get-stripe-connect-status.query.ts really puts in `requirements`. */
type StripeRequirements = { currentlyDue?: string[]; eventuallyDue?: string[]; pastDue?: string[] };
const theRequirements = (value: unknown): StripeRequirements =>
  value !== null && typeof value === 'object' ? (value as StripeRequirements) : {};

@State<StripeConnectStateModel>({
  name: 'stripeConnect',
  defaults,
})
@Injectable()
export class StripeConnectState {
  private readonly _organizersService = inject(OrganizersService);

  @Action(CheckStripeConnectStatusAction)
  checkStripeConnectStatus(ctx: StateContext<StripeConnectStateModel>) {
    ctx.patchState({ isLoading: true, error: null });

    return this._organizersService.getStripeConnectStatus().pipe(
      tap((response) => {
        if (response.error) {
          const errorMessage =
            typeof response.error === 'string' ? response.error : 'Failed to check Stripe Connect status';
          ctx.dispatch(new SetStripeConnectErrorAction(errorMessage));
          return;
        }
        if (!response.value) {
          ctx.dispatch(new SetStripeConnectErrorAction('No response data from server'));
          return;
        }
        const status: StripeConnectStatus = {
          status: response.value.status as unknown as OnboardingStatus,
          chargesEnabled: response.value.chargesEnabled,
          payoutsEnabled: response.value.payoutsEnabled,
          detailsSubmitted: response.value.detailsSubmitted,
          requirements: {
            // The generated client declares requirements as `object`, the empty
            // type, because the response DTO carries no @ApiProperty for it.
            // get-stripe-connect-status.query.ts declares all three as string[].
            currentlyDue: theRequirements(response.value.requirements).currentlyDue ?? [],
            eventuallyDue: theRequirements(response.value.requirements).eventuallyDue ?? [],
            pastDue: theRequirements(response.value.requirements).pastDue ?? [],
          },
          accountId: response.value.accountId,
          checkFailure: theCheckFailure(response.value),
        };
        ctx.dispatch(new SetStripeConnectStatusAction(status));
      }),
      catchError((error) => {
        ctx.dispatch(new SetStripeConnectErrorAction(error?.message || 'Failed to check Stripe Connect status'));
        return throwError(() => error);
      }),
    );
  }

  @Action(SetStripeConnectStatusAction)
  setStripeConnectStatus(ctx: StateContext<StripeConnectStateModel>, { payload }: SetStripeConnectStatusAction) {
    ctx.patchState({
      accountStatus: payload,
      isLoading: false,
      error: null,
    });
  }

  @Action(StartOnboardingAction)
  startOnboarding(ctx: StateContext<StripeConnectStateModel>) {
    ctx.patchState({ isLoading: true, error: null });

    return this._organizersService.createOnboardingLink().pipe(
      tap((response) => {
        console.log('StartOnboardingAction response:', JSON.stringify(response, null, 2));
        if (response.error) {
          const errorMessage = typeof response.error === 'string' ? response.error : 'Failed to create onboarding link';
          ctx.dispatch(new SetStripeConnectErrorAction(errorMessage));
          return;
        }
        if (!response.value) {
          console.error('No response.value in response:', JSON.stringify(response, null, 2));
          ctx.dispatch(new SetStripeConnectErrorAction('No response data from server'));
          return;
        }
        if (!response.value.onboardingUrl) {
          console.error('No onboardingUrl in response.value:', JSON.stringify(response.value, null, 2));
          ctx.dispatch(new SetStripeConnectErrorAction('No onboarding URL in response'));
          return;
        }
        console.log('Setting onboarding URL:', response.value.onboardingUrl);
        ctx.dispatch(
          new SetOnboardingUrlAction({
            url: response.value.onboardingUrl,
            expiresAt: response.value.expiresAt || Date.now() + 3600000,
          }),
        );
      }),
      catchError((error) => {
        console.error('Error in StartOnboardingAction:', error);
        ctx.dispatch(new SetStripeConnectErrorAction(error?.message || 'Failed to create onboarding link'));
        return throwError(() => error);
      }),
    );
  }

  @Action(SetOnboardingUrlAction)
  setOnboardingUrl(ctx: StateContext<StripeConnectStateModel>, { payload }: SetOnboardingUrlAction) {
    ctx.patchState({
      onboardingUrl: payload.url,
      onboardingUrlExpiresAt: payload.expiresAt,
      isLoading: false,
      error: null,
    });
  }

  @Action(CompleteOnboardingAction)
  completeOnboarding(ctx: StateContext<StripeConnectStateModel>) {
    ctx.patchState({
      onboardingUrl: null,
      onboardingUrlExpiresAt: null,
      isLoading: false,
    });
    ctx.dispatch(new CheckStripeConnectStatusAction());
  }

  @Action(LoadOnboardingStatusAction)
  loadOnboardingStatus(ctx: StateContext<StripeConnectStateModel>) {
    return ctx.dispatch(new CheckStripeConnectStatusAction());
  }

  @Action(SetStripeConnectErrorAction)
  setStripeConnectError(ctx: StateContext<StripeConnectStateModel>, { payload }: SetStripeConnectErrorAction) {
    ctx.patchState({
      error: payload,
      isLoading: false,
    });
  }

  @Action(ResetStripeConnectStateAction)
  resetStripeConnectState(ctx: StateContext<StripeConnectStateModel>) {
    ctx.setState(defaults);
  }

  @Action(ClearOnboardingUrlAction)
  clearOnboardingUrl(ctx: StateContext<StripeConnectStateModel>) {
    ctx.patchState({
      onboardingUrl: null,
      onboardingUrlExpiresAt: null,
    });
  }
}
